import React, { useEffect, useRef, useState, useCallback } from "react"
import { format, select, ascending, arc, pie, scaleLinear, ScaleLinear, PieArcDatum } from "d3"
import { Question, QuestionData } from "../../global-interfaces";
import "./style.scss";

const pct_format: any = format(".0%");
const padding = 20;

interface Props {
  chart: Question;
  color: string;
}

export default function PieLegend({ chart, color } : Props) {
  const [setRef, [width, height], svgRef] = useResponsiveSize()
  const { data } = chart
  const radius = (Math.min(width, height) - padding) / 2

  const opacityScale: ScaleLinear<number, number> = scaleLinear()
    .domain([data.length, 0])
    .range([0.2,1]);

  useEffect(() => {
    const pieRef = select(svgRef).select(".pie");
    const pieData = generatePie(data)

    function generatePie( data: QuestionData[] ) {
      return pie<QuestionData>()
        .sort((a: QuestionData, b: QuestionData) => ascending(+a.data_id, +b.data_id))
        .value((d: QuestionData) => d.value)
        (data)
    }
    
    function generateArcs(data: PieArcDatum<QuestionData>) {
      return arc<PieArcDatum<QuestionData>>()
      .innerRadius(0)
      .outerRadius(radius)
      .cornerRadius(1)(data)
    }

    const slices = pieRef.selectAll('.slice')
      .data(pieData)
      .join('path')
      .attr("class", "slice")
      .attr("d", generateArcs)
      .attr("fill", color)
      .style("opacity", (_:any, i:number) => opacityScale(i) || 1)

  }, [width, height])

  return (
    <div className="PieLegend">
      <svg className="pie-svg" ref={(ref) => ref && setRef(ref)} width="100%" height={width}>
        <g className="pie" transform={`translate(${(width - padding)/2} ${(height - padding)/2})`} />
      </svg>
      <div className="pie-legend">
        <div className="category selected">{chart.category}</div>
        {data.map((d: QuestionData, i:number) => 
          <LegendEntry data={d} backgroundColor={color} opacity={opacityScale(i) || 0} />
        )}
      </div>
    </div>
  )
}

function LegendEntry({ data, backgroundColor, opacity } : { data: QuestionData, backgroundColor: string, opacity: number }) {
  return (
    <div className="legend-row">
      <div className="label">
        <span className="dot" style={{ backgroundColor, opacity }}/>
        <span>{data.label}</span>
      </div>
      <span className="value">{pct_format(data.value)}</span>
    </div>
  )
}

function useResponsiveSize ( startingDims?:[number, number] ) : [
  (node: HTMLElement | SVGSVGElement) => void, [number, number], HTMLElement | null 
] {
  const RESIZE_TIMOUT = 200; // duration ms to wait before triggering resize

  const [[width, height], setDimensions] = useState(startingDims || [0, 0]);
  const [targetNode, setTargetNode] = useState<HTMLDivElement | null>(null);
  const resizeTimer = useRef<NodeJS.Timeout>(); // to keep resize handler from firing too often

  // callback when component mounts to set the current node as the target
  const setRef = useCallback((node) => {
    setTargetNode(node);
  }, []);

  const handleResize = useCallback(
    () => {
      if (targetNode) {
        const { width: newWidth, height: newHeight } = targetNode.getBoundingClientRect();
        setDimensions([newWidth, newHeight]);
      }
    },
    [targetNode],
  );
  // on load add resize listener and set width
  useEffect(() => {
    handleResize();
    window.addEventListener('resize', () => {
      //@ts-ignore
      clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(handleResize, RESIZE_TIMOUT);
    });

    return () => window.removeEventListener('resize', () => {
      //@ts-ignore
      clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(handleResize, RESIZE_TIMOUT);
    });
  }, [targetNode]);

  // returns a method to set the target node and the most recently interesected entry
  return [setRef, [width, height], targetNode];
};